<template>
	<aside
		class="property"
		:class="{ 'shrink': !isVisible }">
		<div class="d-flex align-items-start justify-content-between">
			<div v-if="isLoading" class="absolute loader-circle">
				<div class="is-loading"></div>
			</div>
			<div class="property-title">
				{{ $t('Browser Source Properties') }} - {{ label ? label.toUpperCase() : 'Default Browser Source' }}
			</div>
			<button
				class="btn btn-default btn-sm btn-icon property-toggle"
				@click="isVisible = !isVisible">
				<i class="material-icons" v-if="isVisible">chevron_left</i>
				<i class="material-icons" v-else>chevron_right</i>
			</button>
		</div>
		<!-- <transition name="page"> -->
			<div class="property-content pr-2" :class="{'pointer-events-none': isLocked || isActiveEdit  ? isLocked.is_locked || isActiveEdit : true}">
				<div class="form-group mb-4">
					<label class="text-primary">{{ $t('Browser Source Property') }}</label>
					<custom-input
						class="mb-3"
						align="left"
						:label="$t('Item Label')"
						:required="isRequired"
						:max="40"
						@blur="save"
						@keyenter="save"
						v-model="label"
						:restrictChar="true"
					/>
					<div class="row">
						<div class="col-md-12">
							<custom-input
								class="mb-3"
								align="left"
								:label="$t('URL')"
								:required="isRequired"
								@input="onKeywordChange"
								@keyenter="save"
								v-model="customPageUrl"
								:max="255"
							/>
						</div>
					</div>
					<div class="d-flex">
						<div class="form-group mb-3 col-md-6 ml-0 pl-0 mr-3 pr-1">
							<custom-input
								type="number"
								class="form-control"
								field="index"
								:label="$t('Layer')"
								:required="isRequired"
								:max="100"
								:index-duplicated="isIndexDuplicated"
								@blur="save"
								@keyenter="onInputEnter('layer', ...arguments)"
								v-model="layerIndex"
							/>
						</div>
						<!-- form Mute -->
						<md-switch
							class="md-primary mt-1 mb-3 mr-3"
							@change="save"
							v-model="isShowLabel">
							{{ $t('Show Label') }}
						</md-switch>
					</div>
				</div>
				<!-- Border -->
				<div>
					<div class="d-flex justify-content-between">
						<div>
							<label class="text-primary">{{ $t('Border') }}</label>
						</div>
						<div class="ml-0 pl-0">
							<md-switch
								class="md-primary mt-0 mb-2 mr-0"
								@change="save"
								v-model="isEnableBorder">
							</md-switch>
						</div>
					</div>
				</div>
				<div class="row mb-0 pb-0">
					<div class="col-md-6 pr-1 mr-0">
						<custom-input
							class="mb-4 pb-0"
							type="number"
							:label="$t('Thickness')"
							:required="isRequired"
							:min="0"
							:max="1000"
							:step="1"
							:disabled="!isEnableBorder"
							v-model="borderThickness"
							@input="onKeywordChange"
							@keyenter="onInputEnter('borderThickness', ...arguments)"
							@blur="save"
						/>
					</div>
					<div class="col-md-6 pl-1 ml-0">
						<custom-input
							type="color"
							align="left"
							picker-position="right"
							:label="$t('Color')"
							:required="isRequired"
							:class="{'disabled': !isEnableBorder}"
							:isEnableBorder="isEnableBorder"
							:disabled="!isEnableBorder"
							@input="onKeywordChange"
							@keyenter="save"
							v-model="borderColor"
							@blur="save"
						/>
					</div>
				</div>
				<div class="mt-1">
					<div class="d-flex justify-content-between">
						<div>
							<label class="text-primary">{{ $t('Crop media') }}</label>
						</div>
						<div class="ml-0 pl-0">
							<md-switch
								class="md-primary mt-0 mb-2 mr-0"
								@change="save"
								v-model="isEnableCrop">
							</md-switch>
						</div>
					</div>
				</div>
				<div class="row">
					<div class="col-md-6 pr-1 mr-0">
							<custom-input
								class="mb-3"
								type="number"
								:label="$t('Top')"
								:required="isRequired"
								:min="0"
								:max="8000"
								:step="1"
								:suffix="'px'"
								v-model="cropTop"
								:disabled="!isEnableCrop"
								@input="onKeywordChange"
								@keyenter="onInputEnter('top', ...arguments)"
								@blur="save"
							/>
						</div>
						<div class="col-md-6 pl-1 ml-0">
							<custom-input
								class="mb-3"
								type="number"
								:label="$t('Bottom')"
								:required="isRequired"
								:min="0"
								:max="8000"
								:step="1"
								:suffix="'px'"
								v-model="cropBottom"
								:disabled="!isEnableCrop"
								@input="onKeywordChange"
								@keyenter="onInputEnter('bottom', ...arguments)"
								@blur="save"
							/>
						</div>
						<div class="col-md-6 pr-1 mr-0">
							<custom-input
								class="mb-3"
								type="number"
								:label="$t('Left')"
								:required="isRequired"
								:min="0"
								:max="8000"
								:step="1"
								:suffix="'px'"
								v-model="cropLeft"
								:disabled="!isEnableCrop"
								@input="onKeywordChange"
								@keyenter="onInputEnter('left', ...arguments)"
								@blur="save"
							/>
						</div>
						<div class="col-md-6 pl-1 ml-0">
							<custom-input
								class="mb-4"
								type="number"
								:label="$t('Right')"
								:required="isRequired"
								:min="0"
								:max="8000"
								:step="1"
								:suffix="'px'"
								v-model="cropRight"
								:disabled="!isEnableCrop"
								@input="onKeywordChange"
								@keyenter="onInputEnter('right', ...arguments)"
								@blur="save"
							/>
						</div>
				</div>
				<div class="form-group mt-1">
						<!-- Size & Position -->
					<position-settings
						class="mb-3"
						:width.sync="width"
						:height.sync="height"
						:position.sync="position"
						:x.sync="x"
						:y.sync="y"
						:isRatio.sync="isRatio"
						@reset="resetSizePosition"
						@change="onKeywordChange"
						@keyenter="save"
					/>
				</div>
				<!-- on complete label -->
					<!-- <div class="mt-2">
							<div class="d-flex justify-content-between">
								<div>
									<label class="text-primary">{{ $t('On Complete') }}</label>
								</div>
							</div>
					</div> -->
					<!-- on complete controller -->
					<!-- <div class="">
						<md-menu
							:md-active.sync="isOnComplete"
							class="h-fc col-md-12 pr-0 pl-0"
							md-size="small"
							md-direction="top-start"
							md-align-trigger>
							<div v-if="selectedOnComplete"
								class="form-control cur-p relative mb-4"
								md-menu-trigger>
								<item-option
									:item="selectedOnComplete"
									v-if="selectedOnComplete"
								/>
								<div class="text-right">
									<div @click.stop="removeOnComplete" class="modal-close-assets absolute delete-complete-add">
										<i class="material-icons">cancel</i>
									</div>
								</div>
							</div>
							<div v-else md-menu-trigger>
								<input
									type="text"
									class="form-control mb-4"
									:placeholder="$t('Stop (default)')"
									@input="onKeywordChange"
									v-model="keywordSearchOnComplete"
									@click.stop="toggleOnComplete"
								/>
								<item-option
									:item="selectedOnComplete"
									v-if="selectedOnComplete"
								/>
							</div>
							<md-menu-content id="scrolls-style"
							:class="{'macro-content-wrapper-list': filteredParent.length > 6, 'assets-wrapper-list-empty': filteredParent.length === 0,
								'assets-wrapper-list-1': filteredParent.length === 1, 'assets-wrapper-list-2': filteredParent.length === 2,
								'assets-wrapper-list-3': filteredParent.length === 3, 'assets-wrapper-list-4': filteredParent.length === 4,
								'assets-wrapper-list-5': filteredParent.length === 5, 'assets-wrapper-list-6': filteredParent.length === 6,
								}"
								>
								<md-menu-item class="item-macros-rundown macro-content-wrapper-list"
									v-for="rundownItem in filteredParent"
									:key="rundownItem.id"
									@click="selectOnComplete(rundownItem)">
									<item-option :item="rundownItem" />
								</md-menu-item>
								<md-menu-item v-if="filteredParent.length <= 0" class="item-macros-rundown macro-content-wrapper-list">
									Oops, there is no assets available
								</md-menu-item>
							</md-menu-content>
						</md-menu>
					</div> -->
				<!-- parent controller -->
				<div class="mt-2">
					<div class="d-flex justify-content-between">
						<div>
							<label class="text-primary">{{ $t('Parent Controller') }}</label>
						</div>
						<div class="ml-0 pl-0">
							<md-switch
								class="md-primary mt-0 mb-2 mr-0"
								@change="save()"
								v-model="isParent">
							</md-switch>
						</div>
					</div>
					</div>
					<div class="d-flex select ">
						<!-- <div class="select w-75 relative">
							<div class="mb-2 mh-40px mr-4">
								<multiselect class="select-input" v-model="parentItem" select-label="" deselect-label="Remove" track-by="id" label="name"
								openDirection="bottom" :optionsLimit="10" placeholder="Search or Select Parent Item" :options="filteredParent" @input="save()"
								:searchable="true" :allow-empty="true" :show-labels="true" :selectedLabel="''">
								<span class="arrow-multiselect" slot="caret">
									<i class="material-icons pb-1 mt-1 ml-2 size-arrow">expand_more</i>
								</span>
								<template slot="singleLabel" slot-scope="{ option }">{{ option.name }}</template>
								</multiselect>
							</div>
						</div> -->
						<!-- new dropdown select assets -->
						<md-menu
							:md-active.sync="isToggleParent"
							class="h-fc mr-2"
							md-size="small"
							md-direction="bottom-start"
							md-align-trigger>
							<div v-if="selectedParent"
								class="form-control cur-p relative w-230px"
								md-menu-trigger>
								<item-option
									:item="selectedParent"
									v-if="selectedParent"
								/>
								<div @click.stop="removeParent" class="modal-close-assets absolute delete-assets-add">
									<i class="material-icons">cancel</i>
								</div>
							</div>
							<div v-else md-menu-trigger>
								<input
									type="text"
									class="form-control w-230px"
									:placeholder="$t('Search or Select Parent Item')"
									@input="onKeywordChange"
									v-model="keywordSearchParent"
									@click.stop="toggleParent"
								/>
								<item-option
									:item="selectedParent"
									v-if="selectedParent"
								/>
							</div>
							<md-menu-content id="scrolls-style" class="macro-content-wrapper-list">
								<md-menu-item class="item-macros-rundown macro-content-wrapper-list"
									v-for="rundownItem in filteredParent"
									:key="rundownItem.id"
									@click="selectParent(rundownItem)">
									<item-option :item="rundownItem" />
								</md-menu-item>
								<md-menu-item v-if="filteredParent.length <= 0" class="item-macros-rundown macro-content-wrapper-list">
									Oops, there is no assets available
								</md-menu-item>
							</md-menu-content>
						</md-menu>
						<!-- delay -->
						<div class="select">
							<custom-input
								class="form-input mb-3"
								type="number"
								:label="$t('Delay')"
								:min="-15000"
								:max="15000"
								:step="100"
								:suffix="'ms'"
								v-model="childOrder"
								@input="onKeywordChange"
								@keyenter="onInputEnter('delay', ...arguments)"
								@blur="save"
								:allowNegative="true"
							/>
							<!-- <div class="mb-2 mh-60px">
								<multiselect class="select-input" v-model="childOrder" select-label=""
									track-by="id" label="name" openDirection="bottom" placeholder="No Delay" :options="delayList" @input="save()"
									:searchable="true" :allow-empty="true" :show-labels="false">
									<span class="arrow-multiselect" slot="caret">
										<i class="material-icons pb-1 mt-1 ml-2 size-arrow">expand_more</i>
									</span>
									<template slot="singleLabel" slot-scope="{ option }">{{ option.name }}</template>
								</multiselect>
							</div> -->
						</div>
					</div>
			</div>
		<!-- </transition> -->
	</aside>
</template>

<script>
import { mapGetters } from 'vuex';
// import Multiselect from 'vue-multiselect';
import ItemOption from '@/components/ItemOption.vue';
import { delay } from '@/lib/helper';
import rundownItemsApi from '@/api/rundownItems';
// import fileApi from '@/api/file';
import CustomInput from '@/components/CustomInput.vue';
import PositionSettings from '@/components/PositionSettings.vue';
import {
	delayList,
} from '@/lib/statics';

export default {
	name: 'CustomPageProperty',
	props: {
		data: {
			type: Object,
		},
		rundown: {
			type: Object,
		},
		media: {
			type: Object,
		},
		filteredItems: {
			type: Array,
		},
		items: {
			type: Array,
			default: () => [],
		},
	},
	components: {
		CustomInput,
		PositionSettings,
		// Multiselect,
		ItemOption,
	},
	data() {
		return {
			customPageUrl: '',
			isLoading: false,
			label: '',
			layerIndex: 0,
			width: 0,
			height: 0,
			position: '',
			x: 0,
			y: 0,
			type: 'custom_page',
			progress: 0,
			isUploading: false,
			isSaving: false,
			isVisible: true,
			isRequired: false,
			cropTop: 0,
			cropBottom: 0,
			cropLeft: 0,
			cropRight: 0,
			isEnableCrop: false,
			isEnableBorder: false,
			borderColor: '#FFFFFF',
			borderThickness: 1,
			isActiveEdit: false,
			isRatio: true,
			parentItem: null,
			isParent: false,
			delayList,
			childOrder: 0,
			selectedParent: null,
			selectedOnComplete: null,
			keywordSearchParent: '',
			keywordSearchOnComplete: '',
			isToggleParent: false,
			isOnComplete: false,
			isShowLabel: 0,
		};
	},
	methods: {
		toggleParent() {
			this.isToggleParent = true;
		},
		toggleOnComplete() {
			this.isOnComplete = true;
		},
		updateActiveEdit() {
			this.isSaving = true;
			const callback = (response) => {
				const item = response.data;
				this.$emit('add', item);
				this.isSaving = false;
			};
			const errorCallback = () => {
				this.isSaving = false;
			};
			const params = {
				active_edit: null,
			};
			rundownItemsApi.update(this.data.id, params, callback, errorCallback);
		},
		initData() {
			this.isVisible = true;
			this.reset();
			if (this.data) {
				let content = this.data.content;
				this.label = this.data.name;
				if (typeof content === 'string') {
					content = JSON.parse(content);
				}
				const activeEdit = this.data.active_edit;
				if (activeEdit && activeEdit !== this.userId) {
					this.isActiveEdit = true;
				}
				if (content) {
					this.customPageUrl = content.customPageUrl;
					this.layerIndex = this.data.layer_index;
					this.width = content.width;
					this.height = content.height;
					this.position = content.position;
					this.x = content.x;
					this.y = content.y;
					this.type = content.Type;
					this.isRatio = content.isRatio;
					this.cropTop = content.cropTop || 0;
					this.cropBottom = content.cropBottom || 0;
					this.cropLeft = content.cropLeft || 0;
					this.cropRight = content.cropRight || 0;
					this.isEnableCrop = content.isEnableCrop;
					this.isEnableBorder = content.isEnableBorder;
					this.isShowLabel = content.isShowLabel;
					this.borderColor = content.borderColor || '#FFFFFF';
					this.borderThickness = content.borderThickness || 0;
					this.isParent = content.isParent || false;
					// this.parentItem = this.filteredParent.find(item => item.id === content.parentItem) || '';
					this.selectedParent = this.filteredParent.find(item => item.id === content.parentItem) || '';
					this.selectedOnComplete = this.filteredParent.find(item => item.id === content.onCompleteItem) || '';
					this.childOrder = content.childOrder || 0;
				}
			}
		},
		reset() {
			this.customPageUrl = '';
			this.label = '';
			this.layerIndex = 0;
			this.width = 1920;
			this.height = 1080;
			this.position = 'default';
			this.x = 0;
			this.y = 0;
			this.isRatio = true;
			this.cropTop = 0;
			this.cropBottom = 0;
			this.cropLeft = 0;
			this.cropRight = 0;
			this.isEnableCrop = 0;
			this.borderThickness = 0;
			this.isEnableBorder = 0;
			this.isShowLabel = 0;
			this.borderColor = '#FFFFFF';
		},
		onKeywordChange() {
			delay(() => {
				this.save();
				// this.fetch(true);
			}, 1200);
		},
		save() {
			this.isSaving = true;
			const callback = (response) => {
				const item = response.data;
				this.$emit('add', item);
				this.isSaving = false;
			};
			const errorCallback = () => {
				this.isSaving = false;
			};
			if (this.data) {
				rundownItemsApi.update(this.data.id, this.customPageParams, callback, errorCallback);
			} else {
				rundownItemsApi.create(this.customPageParams, callback, errorCallback);
			}
		},
		checkDuplicateIndex() {
			const valueArr = this.filteredItems.map(a => a.layer_index);
			let count = 0;
			for (let i = 0; i < valueArr.length; ++i) {
				if (valueArr[i] === this.layerIndex) count++;
			}
			if (count > 1) {
				return true;
			}
			return false;
		},
		duplicateLayerText() {
			const text1 = 'Other item also using this layer, only one item at a time will be displayed';
			return text1;
		},
		resetSizePosition() {
			this.x = 0;
			this.y = 0;
			this.position = 'default';
			this.width = 1920;
			this.height = 1080;
			this.save();
		},
		selectParent(item) {
			this.selectedParent = item;
			this.keywordSearchParent = '';
			this.save();
		},
		removeParent() {
			this.selectedParent = null;
			this.keywordSearchParent = '';
			this.save();
		},
		selectOnComplete(item) {
			this.selectedOnComplete = item;
			this.keywordSearchOnComplete = '';
			this.save();
		},
		removeOnComplete() {
			this.selectedOnComplete = null;
			this.keywordSearchOnComplete = '';
			this.save();
		},
		onInputEnter(props, value) {
			if (props === 'layer') this.layerIndex = value;
			if (props === 'borderThickness') this.borderThickness = value;
			if (props === 'top') this.cropTop = value;
			if (props === 'bottom') this.cropBottom = value;
			if (props === 'left') this.cropLeft = value;
			if (props === 'right') this.cropRight = value;
			if (props === 'delay') this.childOrder = value;
			this.save();
		},
	},
	computed: {
		...mapGetters({
			user: 'user',
		}),
		filteredParent() {
			const filteredParent = this.items.filter(item => item.id !== this.data.id);
			let finalResult = filteredParent.filter(item => item.type !== 'separator');
			if (this.keywordSearchParent) {
				finalResult = finalResult.filter(item => item.name.toUpperCase().includes(this.keywordSearchParent.toUpperCase()));
				return finalResult;
			}
			if (this.keywordSearchOnComplete) {
				finalResult = finalResult.filter(item => item.name.toUpperCase().includes(this.keywordSearchOnComplete.toUpperCase()));
				return finalResult;
			}
			return finalResult;
		},
		userId() {
			const userId = this.user && this.user.id ? this.user.id : null;
			return userId;
		},
		userSettings() {
			const settings = this.user && this.user.settings ? JSON.parse(this.user.settings) : null;
			return settings;
		},
		customPageParams() {
			// const parentItemId = this.parentItem && this.parentItem.id ? this.parentItem.id : null;
			const parentItemId = this.selectedParent && this.selectedParent.id ? this.selectedParent.id : null;
			const onCompleteItemId = this.selectedOnComplete && this.selectedOnComplete.id ? this.selectedOnComplete.id : null;

			const content = {
				customPageUrl: this.customPageUrl,
				label: this.label,
				layerIndex: this.layerIndex,
				width: this.width,
				height: this.height,
				position: this.position,
				x: this.x,
				y: this.y,
				type: this.type,
				isRatio: this.isRatio,
				cropTop: this.cropTop,
				cropBottom: this.cropBottom,
				cropLeft: this.cropLeft,
				cropRight: this.cropRight,
				isEnableCrop: this.isEnableCrop,
				isEnableBorder: this.isEnableBorder,
				isShowLabel: this.isShowLabel,
				borderColor: this.borderColor,
				borderThickness: this.borderThickness,
				isParent: this.isParent,
				parentItem: parentItemId,
				onCompleteItem: onCompleteItemId,
				childOrder: this.childOrder,
			};
			const contentString = JSON.stringify(content);
			const params = {
				name: this.label || 'Default Browser Source',
				type: this.type,
				content: contentString,
				layer_index: this.layerIndex,
				rundown_id: this.rundownId,
			};
			if (!this.data.active_edit) {
				params.active_edit = this.userId;
			}
			return params;
		},
		rundownId() {
			return this.rundown ? this.rundown.id : 0;
		},
		isIndexDuplicated() {
			if (this.data) {
				// eslint-disable-next-line
				const filter = (({ id, layer_index }) => id !== this.data.id && layer_index === this.layerIndex);
				const duplicateItems = this.items.filter(filter);
				return duplicateItems.length > 0;
			}
			return false;
		},
		isLocked() {
			const index = this.items.findIndex(({ id }) => id === this.data.id);
			return this.items[index];
		},
	},
	watch: {
		data() {
			if (this.data) {
				const activeEdit = this.data.active_edit;
				if (activeEdit && activeEdit !== this.userId) {
					this.isActiveEdit = true;
				} else {
					this.isActiveEdit = false;
				}
				if (this.data) {
					let content = this.data.content;
					this.label = this.data.name;
					if (typeof content === 'string') {
						content = JSON.parse(content);
					}
					if (content) {
						this.customPageUrl = content.customPageUrl;
						this.layerIndex = this.data.layer_index;
						this.width = content.width;
						this.height = content.height;
						this.position = content.position;
						this.x = content.x;
						this.y = content.y;
						this.type = content.Type;

						this.cropTop = content.cropTop || 0;
						this.cropBottom = content.cropBottom || 0;
						this.cropLeft = content.cropLeft || 0;
						this.cropRight = content.cropRight || 0;
						this.isEnableCrop = content.isEnableCrop;
						this.isEnableBorder = content.isEnableBorder;
						this.isShowLabel = content.isShowLabel;
						this.borderColor = content.borderColor || '#FFFFFF';
						this.borderThickness = content.borderThickness || 0;
						this.isParent = content.isParent || false;
						// this.parentItem = this.filteredItems.find(item => item.id === content.parentItem) || '';
						this.selectedParent = this.filteredItems.find(item => item.id === content.parentItem) || '';
						this.selectedOnComplete = this.filteredParent.find(item => item.id === content.onCompleteItem) || '';
						this.childOrder = content.childOrder;
					}
				}
			}
		},
	},
	beforeDestroy() {
		if (this.data.active_edit === this.userId) {
			this.updateActiveEdit();
		}
	},
	created() {
		this.$nextTick(() => {
			this.initData();
		});
		this.isLoading = true;
		this.isRequired = false;
		setTimeout(() => {
			this.isRequired = true;
			this.isLoading = false;
		}, 400);
	},
};
</script>
